import React from "react";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { StyledUISelectRequired } from "../../shared/UI-Library/StyledUISelect";
import { FinancialAssistanceIncomeTiers } from "../types";

type IncomeRange = { value: number; label: string };

export const getIncome = (
  householdSize: number,
  thresholds: Record<number, number[]>,
  t?: TFunction,
): IncomeRange[] => {
  // Default to household size 8 if not found;
  const tiers: number[] = thresholds[householdSize] || thresholds[8];
  if (!tiers) {
    return [];
  }
  const ranges: IncomeRange[] = tiers.map((upperBound, index) => {
    // since the values are in cents we add 100 to the lower bound so there is no overlap
    const lowerBound = index === 0 ? 0 : tiers[index - 1] + 100;
    // since the values are in cents we divide by 100 to get the dollar amount
    const displayLowerBound = lowerBound / 100;
    const displayUpperBound = upperBound / 100;
    const midpoint = Math.floor((lowerBound + upperBound) / 2);
    let label: string;

    if (index === 0) {
      label = t
        ? t("upfrontPayment.financialAssistance.income.lessThan", {
            amount: `$${displayUpperBound.toLocaleString()}`,
          })
        : `Less than $${displayUpperBound.toLocaleString()}`;
    } else {
      label = t
        ? t("upfrontPayment.financialAssistance.income.between", {
            lower: `$${displayLowerBound.toLocaleString()}`,
            upper: `$${displayUpperBound.toLocaleString()}`,
          })
        : `Between $${displayLowerBound.toLocaleString()} and $${displayUpperBound.toLocaleString()}`;
    }

    return { value: midpoint, label };
  });

  // Tack on a final range for the last tier
  const lastLowerBound = tiers[tiers.length - 1];
  const displayLastLowerBound = lastLowerBound / 100;
  ranges.push({
    value: lastLowerBound,
    label: t
      ? t("upfrontPayment.financialAssistance.income.moreThan", {
          amount: `$${displayLastLowerBound.toLocaleString()}`,
        })
      : `More than $${displayLastLowerBound.toLocaleString()}`,
  });

  return ranges;
};

interface HouseholdInfoProps {
  householdInfo: {
    size?: number;
    income?: number;
    incomeDisplay?: string;
  };
  setHouseholdInfo: React.Dispatch<
    React.SetStateAction<{
      size?: number;
      income?: number;
      incomeDisplay?: string;
    }>
  >;
  incomeTiers: FinancialAssistanceIncomeTiers;
}

export const StepHouseholdInfo: React.FC<HouseholdInfoProps> = ({
  householdInfo,
  setHouseholdInfo,
  incomeTiers,
}) => {
  const { t } = useTranslation();
  const getSelectValue = (value?: number): string => String(value ?? "");

  const handleSizeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setHouseholdInfo({ ...householdInfo, size: Number(event.target.value) });
  };

  const handleIncomeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (event.target.selectedOptions.length === 0) {
      return;
    }
    setHouseholdInfo({
      ...householdInfo,
      income: Number(event.target.value),
      incomeDisplay: event.target.selectedOptions[0].text,
    });
  };

  const householdSizes = Object.keys(incomeTiers);

  return (
    <>
      <StyledUISelectRequired
        fieldTitle={t("upfrontPayment.financialAssistance.householdSize")}
        value={getSelectValue(householdInfo.size)}
        onChange={handleSizeChange}
        data-testid="household-size-select"
      >
        <option value="" disabled>
          {t("upfrontPayment.financialAssistance.selectOption")}
        </option>
        {householdSizes.map((size, index) => (
          <option key={size} value={size}>
            {index === householdSizes.length - 1
              ? t("upfrontPayment.financialAssistance.orMore", {
                  count: Number(size),
                })
              : size}
          </option>
        ))}
      </StyledUISelectRequired>

      {householdInfo.size && (
        <StyledUISelectRequired
          fieldTitle={t("upfrontPayment.financialAssistance.householdIncome")}
          value={getSelectValue(householdInfo.income)}
          onChange={handleIncomeChange}
          data-testid="income-select"
        >
          <option value="" disabled>
            {t("upfrontPayment.financialAssistance.selectOption")}
          </option>
          {getIncome(householdInfo.size, incomeTiers, t).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </StyledUISelectRequired>
      )}
    </>
  );
};
